import dayjs from "dayjs";

/**
 * Safely parses a date value for DatePicker component
 * @param {string|Date|null|undefined} dateValue - The date value to parse
 * @returns {dayjs.Dayjs|null} - Parsed dayjs object or null if invalid
 */
export const parseDateValue = (dateValue) => {
  if (!dateValue) return null;
  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed : null;
};

/**
 * Creates a date accept handler for DatePicker components
 * @param {Object} options - Configuration options
 * @param {Function} options.onChange - Function to call when date changes (receives event object)
 * @param {Function} options.onError - Optional function to call on validation error (receives error message)
 * @param {string} options.format - Date format for storage (default: "YYYY-MM-DD" for API)
 * @param {string} options.fieldName - Field name for error messages (default: "date")
 * @param {Function} options.clearErrors - Optional function to clear field errors
 * @returns {Function} - Handler function for DatePicker onChange
 */
export const createDateAcceptHandler = ({
  onChange,
  onError,
  format = "YYYY-MM-DD",
  fieldName = "date",
  clearErrors,
}) => {
  return (val) => {
    // Clear errors if function provided
    if (clearErrors) {
      clearErrors(fieldName);
    }

    // If empty, set to null
    if (!val) {
      onChange({
        target: { name: fieldName, value: null },
      });
      return;
    }

    // If invalid date, show error to user and set to null
    if (!val.isValid()) {
      const errorMessage = `Invalid date entered for ${fieldName.replace(/_/g, " ")}. Please enter a valid date.`;

      if (onError) {
        onError(errorMessage);
      }

      onChange({
        target: { name: fieldName, value: null },
      });
      return;
    }

    // Format and save date
    onChange({
      target: { name: fieldName, value: val.format(format) },
    });
  };
};
